import { useState } from 'react';
import { Button } from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import { Textarea } from '../../../components/inputs/Textarea';
import { useToast } from '../../../components/toastContext';
import { ApiError } from '../../../lib/apiError';
import { focusValidationError } from '../../../lib/validationFocus';
import { approvalsApi } from '../approvalsApi';
import { decisionMessage } from '../approvalTypes';

/**
 * Refuses a request, with the reason.
 *
 * **A FORM rather than a `<ConfirmDialog>`, because it COLLECTS.** The reason is required and is
 * the only thing the submitter is told — "rejected" with nothing beside it leaves them
 * resubmitting the same record. A confirm dialog cannot ask for a value, and stacking one over
 * this form would break "one modal, never stacked", so the reason and the confirm tick sit on
 * one form together.
 *
 * **Every OTHER decision confirms too**, and the sentence here used to say the opposite —
 * "approving needs no dialog… that one commits from the row". Product call, 29 Sep 2026:
 * approving is irreversible and advances another module's record, so it asks first. The three
 * that collect nothing are `<ConfirmDialog requireConfirm>` on the profile.
 */
export function RejectApprovalModal({
  open,
  request,
  stepCount,
  onClose,
  onRejected,
}: {
  open: boolean;
  /**
   * The narrowest shape both callers can satisfy — the list row and the detail, which count
   * their steps differently. A `stepCount` beside it rather than inside it is what lets the
   * profile pass `steps.length` and the inbox pass `totalSteps` without either of them
   * reshaping the other's DTO.
   */
  request: { id: string; subjectReference: string | null };
  /** How many approvers are on the chain — the consequence line reads differently at one. */
  stepCount: number;
  onClose: () => void;
  onRejected: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = reason.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await approvalsApi.reject(request.id, reason.trim());

      /*
        THE CONFIRMATION IS RAISED HERE rather than by each caller, so the inbox and the
        profile cannot word one act two ways — and the modal is the only surface that knows
        the reason was accepted.

        The FAILURE deliberately does NOT toast: it keeps the form open, so the strip below
        and the field `focusValidationError` lands on are where the reader is already
        standing. A toast beside them would be the same message in two slots.
      */
      toast.success(decisionMessage('Rejected', request.subjectReference));
      onRejected();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not record the rejection.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'reject-approval' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={request.subjectReference ? `Reject ${request.subjectReference}` : 'Reject request'}
      size="lg"
      id="reject-approval-modal"
      /*
        NO Autogenerate. A rejection reason is a judgement somebody is making about a specific
        record — generating one would put words in an approver's mouth, which is the one place
        the generator would be actively wrong rather than merely unhelpful.
      */
      confirm={{
        id: 'reject-approval-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="reject-approval-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="reject-approval-submit"
            variant="danger"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Rejecting"
          >
            Reject
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          THE CONSEQUENCE BEFORE THE TICK. One rejection ends the request whoever is still
          waiting on it, which is not obvious from a chain of several approvers on screen.
        */}
        <p className="text-xs text-slate-600">
          This ends the request.{' '}
          {stepCount > 1
            ? 'The approvers who have not yet signed will not be asked, and their steps are marked skipped.'
            : 'The record goes back to whoever submitted it.'}
        </p>

        <Textarea
          id="reject-approval-reason"
          label="Reason"
          required
          description="What the submitter has to change. This is the only thing they are told, so name the field or the figure rather than saying it was refused."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={reason.trim() ? error?.fieldError('reason') : 'Say why it is being rejected.'}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
